import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GameWrapper from '@/pages/jeux/components/GameWrapper';
import GameOverModal from '@/pages/jeux/components/GameOverModal';
import { useGame } from '@/contexts/GameContext';
import { HEALTH_TIPS } from '@/mocks/jeux-data';

type Difficulty = 'Facile' | 'Moyen' | 'Difficile';

// Generate a solvable sudoku board
function generateBoard(diff: Difficulty): { puzzle: number[][]; solution: number[][] } {
  const base: number[][] = [
    [5, 3, 4, 6, 7, 8, 9, 1, 2],
    [6, 7, 2, 1, 9, 5, 3, 4, 8],
    [1, 9, 8, 3, 4, 2, 5, 6, 7],
    [8, 5, 9, 7, 6, 1, 4, 2, 3],
    [4, 2, 6, 8, 5, 3, 7, 9, 1],
    [7, 1, 3, 9, 2, 4, 8, 5, 6],
    [9, 6, 1, 5, 3, 7, 2, 8, 4],
    [2, 8, 7, 4, 1, 9, 6, 3, 5],
    [3, 4, 5, 2, 8, 6, 1, 7, 9],
  ];

  const blanks = diff === 'Facile' ? 35 : diff === 'Moyen' ? 45 : 55;
  const puzzle = base.map(row => [...row]);
  let removed = 0;
  while (removed < blanks) {
    const r = Math.floor(Math.random() * 9);
    const c = Math.floor(Math.random() * 9);
    if (puzzle[r][c] !== 0) {
      puzzle[r][c] = 0;
      removed++;
    }
  }

  return { puzzle, solution: base };
}

export default function SudokuGame() {
  const navigate = useNavigate();
  const { startGame, endGame, gameStatus, gameScore, gameTime, gameErrors, updateScore, updateErrors, unlockAchievement } = useGame();
  const [difficulty, setDifficulty] = useState<Difficulty | null>(null);
  const [puzzle, setPuzzle] = useState<number[][]>([]);
  const [solution, setSolution] = useState<number[][]>([]);
  const [playerGrid, setPlayerGrid] = useState<number[][]>([]);
  const [selected, setSelected] = useState<[number, number] | null>(null);
  const [showGameOver, setShowGameOver] = useState(false);
  const [started, setStarted] = useState(false);
  const [notes, setNotes] = useState<Set<string>>(new Set());

  const handleStart = (d: Difficulty) => {
    setDifficulty(d);
    const { puzzle: p, solution: s } = generateBoard(d);
    setPuzzle(p);
    setSolution(s);
    setPlayerGrid(p.map(row => [...row]));
    setStarted(true);
    setShowGameOver(false);
    startGame('sudoku');
  };

  const handleCellClick = (r: number, c: number) => {
    if (puzzle[r][c] !== 0) return;
    setSelected([r, c]);
  };

  const handleNumberInput = useCallback((num: number) => {
    if (!selected) return;
    const [r, c] = selected;
    if (puzzle[r][c] !== 0) return;
    const newGrid = playerGrid.map(row => [...row]);
    newGrid[r][c] = num;
    setPlayerGrid(newGrid);

    if (num !== solution[r][c]) {
      updateErrors(gameErrors + 1);
    }

    // Check if complete
    const complete = newGrid.every((row, ri) => row.every((cell, ci) => cell === solution[ri][ci]));
    if (complete) {
      const bonus = difficulty === 'Difficile' ? 500 : difficulty === 'Moyen' ? 300 : 200;
      const finalScore = gameScore + bonus + Math.max(0, 900 - gameTime);
      updateScore(finalScore);
      if (difficulty === 'Difficile') unlockAchievement('sudoku_pro');
      endGame(finalScore, gameTime, gameErrors);
      setShowGameOver(true);
    }
  }, [selected, puzzle, solution, playerGrid, difficulty, gameScore, gameTime, gameErrors, updateScore, updateErrors, endGame, unlockAchievement]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!started || gameStatus !== 'playing') return;
      const num = parseInt(e.key);
      if (num >= 1 && num <= 9) handleNumberInput(num);
      if (e.key === 'Backspace' || e.key === 'Delete') {
        if (selected) {
          const [r, c] = selected;
          if (puzzle[r][c] === 0) {
            const newGrid = playerGrid.map(row => [...row]);
            newGrid[r][c] = 0;
            setPlayerGrid(newGrid);
          }
        }
      }
      if (e.key === 'ArrowUp' && selected) setSelected(([r, c]) => [Math.max(0, r - 1), c]);
      if (e.key === 'ArrowDown' && selected) setSelected(([r, c]) => [Math.min(8, r + 1), c]);
      if (e.key === 'ArrowLeft' && selected) setSelected(([r, c]) => [r, Math.max(0, c - 1)]);
      if (e.key === 'ArrowRight' && selected) setSelected(([r, c]) => [r, Math.min(8, c + 1)]);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selected, started, gameStatus, puzzle, playerGrid, handleNumberInput]);

  const handleReplay = () => { if (difficulty) handleStart(difficulty); };
  const handleQuit = () => { endGame(gameScore, gameTime, gameErrors); navigate('/jeux', { replace: true }); };

  if (!started) {
    return (
      <GameWrapper gameName="Sudoku Médical" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-grid-line text-3xl text-accent-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Sudoku Médical</h2>
            <p className="text-foreground-600 text-sm mb-6">Choisis ta difficulté :</p>
            <div className="grid grid-cols-1 gap-3">
              {(['Facile', 'Moyen', 'Difficile'] as Difficulty[]).map((d) => (
                <button key={d} onClick={() => handleStart(d)} className="py-3 px-4 bg-secondary-100 hover:bg-secondary-200 rounded-xl text-sm font-medium text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">{d}</button>
              ))}
            </div>
          </div>
        </div>
      </GameWrapper>
    );
  }

  return (
    <GameWrapper gameName="Sudoku Médical" onQuit={handleQuit}>
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="bg-background-50 border-2 border-foreground-300 rounded-2xl p-1 w-full max-w-md">
          <div className="grid grid-cols-9 gap-px bg-foreground-300">
            {playerGrid.map((row, r) =>
              row.map((cell, c) => {
                const isGiven = puzzle[r][c] !== 0;
                const isSelected = selected && selected[0] === r && selected[1] === c;
                const isError = !isGiven && cell !== 0 && cell !== solution[r][c];
                const borderR = r % 3 === 2 && r < 8 ? 'border-r-2 border-r-foreground-400' : '';
                const borderB = c % 3 === 2 && c < 8 ? 'border-b-2 border-b-foreground-400' : '';

                return (
                  <button
                    key={`${r}-${c}`}
                    onClick={() => handleCellClick(r, c)}
                    className={`aspect-square flex items-center justify-center text-sm font-medium transition-colors cursor-pointer ${
                      isGiven
                        ? 'bg-background-100 text-foreground-950 font-bold'
                        : isSelected
                          ? 'bg-primary-100 text-primary-700'
                          : isError
                            ? 'bg-red-100 text-red-700'
                            : 'bg-background-50 text-foreground-700 hover:bg-primary-50'
                    } ${borderR} ${borderB}`}
                  >
                    {cell !== 0 ? cell : ''}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Number pad */}
        <div className="grid grid-cols-9 gap-1 mt-4 w-full max-w-md">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
            <button
              key={n}
              onClick={() => handleNumberInput(n)}
              className="aspect-square bg-background-100 hover:bg-primary-100 rounded-lg flex items-center justify-center text-sm font-bold text-foreground-950 transition-colors cursor-pointer"
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {showGameOver && (
        <GameOverModal
          score={gameScore}
          timeElapsed={gameTime}
          errors={gameErrors}
          gameName="sudoku"
          message="Sudoku terminé !"
          onReplay={handleReplay}
          onQuit={handleQuit}
          healthTip={HEALTH_TIPS[Math.floor(Math.random() * HEALTH_TIPS.length)]}
        />
      )}
    </GameWrapper>
  );
}